import React, { FC, useState, useEffect } from 'react';
import * as propz from 'propz';
import {
  getTournamentResultsFormattedByYearsShort,
  assignPositions,
} from 'helpers/score';
import { SchoolEvent } from 'models/event';
import { Tournament } from 'models/tournament';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled, experimental_sx as sx } from '@mui/system';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const TableWrapper = styled(Box)(
  sx({
    width: '100%',
    backgroundColor: 'transparent',
  })
);

const TableHeadCellSenior = styled(TableCell)(
  sx({
    fontWeight: 'bold',
    fontSize: '1.6rem',
    bgcolor: '#fafafa',
    color: '#222',
    py: 1.5,
    whiteSpace: 'nowrap',
  })
);

interface Props {
  events: SchoolEvent[];
  tournament: Tournament;
  pageIndex: number;
  pageCount: number;
}

export const TVOverallSeniorByYear: FC<Props> = ({
  events,
  tournament,
  pageIndex,
  pageCount,
}) => {

  const data = getTournamentResultsFormattedByYearsShort(events, tournament);
  const allRows: any[] = propz.get(data, ['scores', 'Overall results'], []);

  const pageSize = 7;
  const totalPages = Math.max(1, Math.ceil(allRows.length / pageSize));

  const safePageIndex =
    pageIndex >= 0 && pageIndex < totalPages
      ? pageIndex
      : 0;

  const keys = Object.keys(allRows[0]);
  const schoolKey = keys.find(
    k => k !== 'Total' && !/^(?:Year\s+|Y\d+)/.test(k)
  );
  const seniorCols = keys.filter(col => {
    const m = col.match(/^(?:Y|Year)\s*([\d/-]+)/);
    if (!m) return false;
    const nums = m[1].match(/\d+/g)!.map(d => parseInt(d, 10));
    return Math.min(...nums) >= 7;
  });
  if (seniorCols.length === 0) return null;

  const seniorOnlyRows = allRows.map(row => {
    let total = 0;
    const out: any = {};
    if (schoolKey) out[schoolKey] = row[schoolKey];
    seniorCols.forEach(col => {
      const v = parseInt(String(row[col]), 10) || 0;
      out[col] = v;
      total += v;
    });
    out.Total = total;
    return out;
  });

  const sorted = [...seniorOnlyRows].sort((a, b) => (b.Total || 0) - (a.Total || 0));
  const ranked = assignPositions(sorted);

  const start = safePageIndex * pageSize;
  const pageRows = ranked.slice(start, start + pageSize);

  const displayCols = schoolKey
    ? [schoolKey, ...seniorCols, 'Total']
    : [...seniorCols, 'Total'];

  return (
    <>
      <TableWrapper>
        <Table
          size="medium"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: '0 8px',
            backgroundColor: 'transparent',
          }}
        >
          <TableHead>
            <TableRow>
              <TableHeadCellSenior align="center">#</TableHeadCellSenior>
              {displayCols.map(col => (
                <TableHeadCellSenior
                  key={col}
                  align={col === 'Team' ? 'left' : 'center'}
                >
                  {col}
                </TableHeadCellSenior>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row, idx) => {
              const totalVal = row.Total || 0;
              const isTop3 = (row.position ?? Infinity) <= 3 && totalVal > 0;
              let bg = idx % 2 === 0 ? '#ffffff' : '#fafafa';
              if (isTop3) {
                bg =
                  row.position === 1
                    ? 'rgba(255,243,224,1)'
                    : row.position === 2
                    ? 'rgba(237,238,242,1)'
                    : 'rgba(243,236,223,1)';
              }
              return (
                <TableRow
                  key={idx}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.85)',
                    borderRadius: '4px',
                    '& td': { borderBottom: 'none' },
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{ fontSize: '1.5rem', px: 0, py: 1 }}
                  >
                    {row.position}
                    {isTop3 && row.position === 1 && (
                      <EmojiEventsIcon
                        sx={{ ml: 1, color: 'gold', fontSize: '1.8rem' }}
                      />
                    )}
                    {isTop3 && row.position === 2 && (
                      <EmojiEventsIcon
                        sx={{ ml: 1, color: 'silver', fontSize: '1.8rem' }}
                      />
                    )}
                    {isTop3 && row.position === 3 && (
                      <EmojiEventsIcon
                        sx={{ ml: 1, color: '#cd7f32', fontSize: '1.8rem' }}
                      />
                    )}
                  </TableCell>
                  {displayCols.map(col => (
                    <TableCell
                      key={col}
                      align={col === 'Team' ? 'left' : 'center'}
                      sx={{
                        fontSize: '1.5rem',
                        px: 2,
                        py: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row[col]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>

      {pageCount > 1 && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#fff',
            }}
          >
            Page {safePageIndex + 1} of {pageCount}
          </Typography>
        </Box>
      )}
    </>
  );
};
